import { useEffect, useRef } from 'react';

/**
 * useReferralWebSocket — opens a WebSocket to /api/ws/analytics and
 * forwards ``referral_new_signup`` messages to the provided callback.
 *
 * Kept intentionally small and single-purpose: the affiliate dashboard
 * is the only consumer today, and the WS control-protocol
 * (subscribe/unsubscribe rooms for per-account analytics) is not
 * exposed here because none of the referral-toast callers need it.
 *
 * Reconnect: if the socket drops (network flap, server restart), we
 * back off and retry up to 5 times with an increasing delay
 * (1s → 2s → 4s → 8s → 16s) so a running-late deploy doesn't spam the
 * server. The 6th failure is silent — the user just won't get toasts
 * until they refresh.
 */
export const useReferralWebSocket = (onSignup) => {
  const socketRef = useRef(null);
  const retryRef = useRef(0);
  const onSignupRef = useRef(onSignup);

  // Keep the callback ref current without triggering a reconnect
  // whenever the caller re-renders with a fresh function ref.
  useEffect(() => { onSignupRef.current = onSignup; }, [onSignup]);

  useEffect(() => {
    let cancelled = false;

    const connect = () => {
      if (cancelled) return;

      // Session token lives in localStorage (mirrored from the httpOnly
      // cookie). If it's missing there's no point trying to auth.
      // Uses the same key `harmonix_st` that ``services/api.js`` writes to.
      const token = typeof window !== 'undefined' ? localStorage.getItem('harmonix_st') : null;
      if (!token) return;

      const base = process.env.REACT_APP_BACKEND_URL || '';
      const wsUrl = base.replace(/^http/, 'ws') + `/api/ws/analytics?token=${encodeURIComponent(token)}`;

      let socket;
      try {
        socket = new WebSocket(wsUrl);
      } catch (err) {
        // Malformed URL / CSP / etc — bail without retrying.
        return;
      }
      socketRef.current = socket;

      socket.onmessage = (evt) => {
        try {
          const msg = JSON.parse(evt.data);
          if (msg?.type === 'referral_new_signup' && typeof onSignupRef.current === 'function') {
            onSignupRef.current(msg.data || {});
          }
        } catch {
          // Non-JSON pings from the server — ignore.
        }
      };

      socket.onopen = () => { retryRef.current = 0; };

      socket.onclose = () => {
        if (cancelled) return;
        if (retryRef.current >= 5) return;
        const delay = Math.min(1000 * 2 ** retryRef.current, 16000);
        retryRef.current += 1;
        setTimeout(connect, delay);
      };

      socket.onerror = () => {
        // Force close so the reconnect path in onclose runs.
        try { socket.close(); } catch { /* noop */ }
      };
    };

    connect();

    return () => {
      cancelled = true;
      try { socketRef.current?.close(); } catch { /* noop */ }
    };
  }, []);
};

export default useReferralWebSocket;
